// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase, db } from '../lib/supabase';

export interface Membership {
  clubId: string;
  clubName?: string;
  clubRole: string;
}

interface Profile {
  id: string;          // players.id
  userId: string;      // auth user id
  displayName: string;
  playerRole?: string;
  city?: string;
  isFreeAgent: boolean;
  /** Every active club_members row for this user. */
  memberships: Membership[];
  /** First active membership — kept for back-compat with existing consumers. */
  clubId?: string;
  clubName?: string;
  clubRole?: string;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, role?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Returns the active club_role for the given clubId, or undefined. */
  roleForClub: (clubId: string | undefined | null) => string | undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession]   = useState<Session | null>(null);
  const [user, setUser]         = useState<User | null>(null);
  const [profile, setProfile]   = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadProfile = useCallback(async (userId: string) => {
    try {
      // LEFT join on club_members so un-clubbed players still load.
      const { data } = await db.players()
        .select(`
          id, display_name, player_role, city, is_free_agent,
          club_members (
            club_id, club_role, is_active,
            clubs ( id, name )
          )
        `)
        .eq('user_id', userId)
        .maybeSingle();

      if (data) {
        const rawMemberships = Array.isArray(data.club_members) ? data.club_members : [];
        const activeMemberships: Membership[] = rawMemberships
          .filter((m: any) => m.is_active && m.club_id)
          .map((m: any) => ({
            clubId: m.club_id,
            clubName: (m.clubs as any)?.name,
            clubRole: m.club_role,
          }));
        const primary = activeMemberships[0];
        setProfile({
          id: data.id,
          userId,
          displayName: data.display_name,
          playerRole: data.player_role,
          city: data.city,
          isFreeAgent: data.is_free_agent,
          memberships: activeMemberships,
          clubId: primary?.clubId,
          clubName: primary?.clubName,
          clubRole: primary?.clubRole,
        });
      }
    } catch (err: unknown) {
      console.error('[AuthContext] Failed to load profile:', err);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id);
  }, [user, loadProfile]);

  useEffect(() => {
    // Single source of truth for initial auth state. Supabase JS v2 fires
    // an INITIAL_SESSION event on subscribe, so running getSession() in
    // parallel races them: getSession can resolve null (cold cache) and
    // flip isLoading=false before INITIAL_SESSION delivers the real
    // session, which bounces ProtectedRoute to /home and forces the user
    // to refresh.
    //
    // The callback MUST NOT await async work — supabase-js holds an
    // internal lock while the listener runs and can deadlock on the next
    // TOKEN_REFRESHED if we await loadProfile here. We snapshot session
    // synchronously and dispatch loadProfile via setTimeout so the lock
    // releases immediately.
    let cancelled = false;
    let initialResolved = false;
    const resolveInitial = () => {
      if (cancelled || initialResolved) return;
      initialResolved = true;
      setIsLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const uid = session.user.id;
        // Detach the async work from the auth callback lock.
        window.setTimeout(() => {
          if (cancelled) return;
          loadProfile(uid).finally(resolveInitial);
        }, 0);
      } else {
        setProfile(null);
        resolveInitial();
      }
    });

    // Fallback for pathological cases (SDK hung on a stuck token refresh,
    // offline at boot) so the loading screen can't latch forever.
    const fallback = window.setTimeout(resolveInitial, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  }, []);

  const register = useCallback(async (email: string, password: string, fullName: string, role: string = 'player') => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName, role } },
    });
    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Sign-up returned no user.');

    // Ensure session is applied before inserting downstream rows (RLS depends on auth.uid()).
    if (!data.session) {
      const { error: siErr } = await supabase.auth.signInWithPassword({ email, password });
      if (siErr) throw new Error('Account created but auto-sign-in failed: ' + siErr.message);
    }
    const userId = data.user.id;

    // Every user gets a players row so they show up in marketplaces / scoring.
    const { error: pErr } = await db.players().insert({
      user_id: userId,
      display_name: fullName,
      player_role: 'Batsman',
      is_free_agent: role === 'player',
      profile_visibility: 'public',
    });
    if (pErr) {
      console.error('Profile creation error:', pErr.message);
      throw new Error('Profile creation failed: ' + pErr.message);
    }

    // Role-specific side rows.
    try {
      if (role === 'authority_admin') {
        const { data: a, error: aErr } = await supabase.from('authorities').insert({
          name: `${fullName} (authority)`,
          created_by: userId,
          is_verified: false,
        }).select('id').single();
        if (aErr) throw aErr;
        const { error: aaErr } = await supabase.from('authority_admins').insert({
          authority_id: a.id,
          user_id: userId,
          role: 'owner',
        });
        if (aaErr) throw aaErr;
      } else if (role === 'umpire') {
        const { error: uErr } = await supabase.from('umpires').insert({
          user_id: userId,
          certification_level: 'local',
          is_available: true,
        });
        if (uErr) throw uErr;
      }
    } catch (e: any) {
      console.error('Role-specific row error:', e?.message ?? e);
      throw new Error('Account created but role setup failed: ' + (e?.message ?? e));
    }
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const roleForClub = useCallback((clubId: string | undefined | null) => {
    if (!clubId || !profile) return undefined;
    return profile.memberships.find(m => m.clubId === clubId)?.clubRole;
  }, [profile]);

  // Memoise the context value so consumer components don't re-render on
  // every parent render — only when the underlying state actually changes.
  // Without this, every useAuth() caller (Layout, every page) re-renders
  // whenever AuthProvider's children re-render for any reason.
  const value = useMemo(() => ({
    session, user, profile,
    isAuthenticated: !!session,
    isLoading, login, register, logout, refreshProfile,
    roleForClub,
  }), [session, user, profile, isLoading, login, register, logout, refreshProfile, roleForClub]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
